import { pick, kebabCase, camelCase } from 'lodash';
import type { EmbedStoryParams } from 'src/sdk/types';
import { DEFAULT_PARAMS, SDK_VERSION_PARAM, SDK_V2 } from 'src/sdk/constants';
import { IS_DEVEL } from 'utils/environment';
import UrlHelper, { addEndingSlash } from 'utils/url-helper';
import { prependUrlProtocol } from 'utils/url/prepend-url-protocol';
import { EMBED_READY_CALLBACK_NAME } from 'client/constants/common';
import { BRIDGE_EVENT_TYPE } from 'client/utils/event-bridge/types';

export function getEmbedCode(
	clientStoryId: string,
	customParams: EmbedStoryParams &
		Partial<{ fixedSize: boolean; width: number; height: number; includeEventBridge: boolean }> = {}
) {
	const buildId = `${process.env.BUILD_ID || '0'}-${Math.round(Math.random())}`;
	const { isStretchHeight, fixedSize, width, height, includeEventBridge } = customParams;
	const rawParams: EmbedStoryParams = pick({ ...DEFAULT_PARAMS, ...customParams }, Object.keys(DEFAULT_PARAMS));
	const processedParams = processParams(rawParams);

	// Convert EmbedStoryParams to data attributes
	const dataAttributes = convertParamsToDataAttributes(processedParams);

	const domain = addEndingSlash(processedParams.domain || `${UrlHelper.client}/${clientStoryId}`);
	const source = `sdk-prod.js?${new URLSearchParams({ buildId, [SDK_VERSION_PARAM]: SDK_V2 }).toString()}`;
	const src = IS_DEVEL ? `/${source}` : prependUrlProtocol(`${domain}${source}`, 'https');
	const id = `story-${clientStoryId}`;

	const css = {
		width: fixedSize ? `${width}px` : '',
		height: fixedSize ? `${isStretchHeight ? '100vh' : `${height}px`}` : `${isStretchHeight ? '100vh' : ''}`,
	};
	const styleAttr = `${css.width ? `width: ${css.width};` : ''}${css.height ? `height: ${css.height};` : ''}`;

	// Convert data attributes to HTML string
	const dataAttrsString = Object.entries(dataAttributes)
		.map(([key, value]) => ` data-${key}='${value}'`)
		.join('');

	const globalScript = `window.${EMBED_READY_CALLBACK_NAME} ??= (story) => {
	story.events.on('${BRIDGE_EVENT_TYPE}', ({ type, data }) => {});
}`;
	const globalScriptTag = includeEventBridge ? `<script>${globalScript}</script>` : '';
	const html = `<div id='${id}'${styleAttr ? ` style='${styleAttr}'` : ''}${dataAttrsString}></div>`;
	const sdkScript = `<script src='${src}' data-id='${clientStoryId}' async></script>`;

	return {
		html: [html, sdkScript, globalScriptTag].filter(Boolean).join('\n'),
		js: buildJavaScriptEmbedCode({
			id,
			css,
			dataAttributes,
			src,
			clientStoryId,
			includeEventBridge,
			globalScript,
		}),
	};
}

interface JavaScriptEmbedParams {
	id: string;
	css: { width: string; height: string };
	dataAttributes: Record<string, string>;
	src: string;
	clientStoryId: string;
	includeEventBridge?: boolean;
	globalScript: string;
}

/**
 * Builds readable JavaScript embed code using template literals
 */
function buildJavaScriptEmbedCode({
	id,
	css,
	dataAttributes,
	src,
	clientStoryId,
	includeEventBridge,
	globalScript,
}: JavaScriptEmbedParams): string {
	// Build data attributes assignment
	const dataAttrsCode = Object.entries(dataAttributes)
		.map(([key, value]) => `c.dataset.${camelCase(key)} = '${value.replace(/'/g, '"')}';`)
		.join(' ');

	// Build the main embed script
	const embedScript = `
		const c = document.createElement('div');
		c.id = '${id}';
		c.style.width = '${css.width}';
		c.style.height = '${css.height}';
		${dataAttrsCode}

		const s1 = document.createElement('script');
		s1.src = '${src}';
		s1.dataset.id = '${clientStoryId}';
		s1.async = true;

		${includeEventBridge ? `const s2 = document.createElement('script'); s2.innerHTML = \`${globalScript}\`;` : ''}

		const t = document.body;
		t.append(c);
		t.append(s1);
		${includeEventBridge ? 't.append(s2);' : ''}
	`
		.replace(/\s+/g, ' ')
		.trim();

	return embedScript;
}

/**
 * Process parameters to include only those that differ from defaults
 * and convert arrays/objects to JSON strings
 */
function processParams(embedParams: EmbedStoryParams): Record<string, string> {
	const result: Record<string, string> = {};

	for (const [key, value] of Object.entries(embedParams)) {
		// Skip default domain parameter
		if (key === 'domain' && typeof value === 'string' && value.includes(UrlHelper.client)) {
			// eslint-disable-next-line no-continue
			continue;
		}

		// Only include parameters that differ from defaults
		if (value !== DEFAULT_PARAMS[key as keyof EmbedStoryParams] && value !== undefined && value !== '') {
			if (Array.isArray(value) || typeof value === 'object') {
				try {
					result[key] = JSON.stringify(value);
				} catch {
					// skip invalid parameters
				}
			} else {
				result[key] = value.toString().replace(/'/g, '"');
			}
		}
	}

	return result;
}

function convertParamsToDataAttributes(embedParams: Record<string, string>): Record<string, string> {
	const dataAttrs: Record<string, string> = {};
	for (const [key, value] of Object.entries(embedParams)) {
		const dataKey = kebabCase(key);
		dataAttrs[dataKey] = value;
	}
	return dataAttrs;
}
